/** Copyright © 2025 Qcells. All rights reserved.
This software is proprietary and confidential. Unauthorized use,
duplication, or distribution of software is strictly prohibited.
*/
import { useI18n } from 'vue-i18n';
import { PublicClientApplication } from '@azure/msal-browser';
import { useAuthentication } from '@hems/portal/src/composables/authentication';
import { acquireTokenSilent, logoutRedirectByAccountInfo, logoutRedirectByTenant, } from '@hems/service/src/api/auth/MSALService';
import { useLoading } from '@hems/util/src/composable/loading';
import { useLogout } from '@hems/util/src/composable/logout';
import { useAlertModal } from '@hems/util/src/composable/modal';
import { AUTH_STATUS } from '@hems/util/src/constant/auth';
import { TENANT } from '@hems/util/src/constant/msal';
import { getLoginRequestParams, getMSALAccountInfo, getHomeownerConfig, getPartnersConfig, getAuthTokenFromCookie, parseJwt, } from '@hems/util/src/helper/authHelper';
import { isLocalEnv } from '@hems/util/src/helper/helper';
export const useMSAL = () => {
    const msalInstanceHomeowner = new PublicClientApplication(getHomeownerConfig());
    const msalInstancePartners = new PublicClientApplication(getPartnersConfig());
    const getMSALInstance = (tenant) => tenant === TENANT.HOMEOWNER ? msalInstanceHomeowner : msalInstancePartners;
    const { startLoading, stopLoading } = useLoading();
    const loginRedirect = async (tenant) => {
        try {
            startLoading();
            await getMSALInstance(tenant).loginRedirect(getLoginRequestParams(tenant));
        }
        catch (error) {
            console.error(error);
        }
        finally {
            stopLoading();
        }
    };
    const getAuthRedirectPromiseResponse = async () => {
        try {
            startLoading();
            // handleRedirectPromise를 호출했을 때 response가 있으면 로그인 처리, 없으면 세션 갱신
            const responseHomeowner = await msalInstanceHomeowner.handleRedirectPromise();
            const responsePartners = await msalInstancePartners.handleRedirectPromise();
            return { responseHomeowner, responsePartners };
        }
        catch (error) {
            console.error(error);
            return { responseHomeowner: null, responsePartners: null };
        }
        finally {
            stopLoading();
        }
    };
    const getActiveAccount = () => {
        const accountPartners = msalInstancePartners.getActiveAccount();
        const accountHomeowner = msalInstanceHomeowner.getActiveAccount();
        return accountHomeowner || accountPartners;
    };
    const getTenantByTenantId = (tenantId) => tenantId === process.env.VUE_APP_MSAL_HOMEOWNER_TENANT_ID ? TENANT.HOMEOWNER : TENANT.PARTNERS;
    const { setTokenInfo } = useAuthentication();
    const { t } = useI18n();
    const loginErrorAlertModal = useAlertModal({ content: t('message.login_server_error') });
    const processLogin = async ({ responseHomeowner, responsePartners }) => {
        try {
            startLoading();
            const response = responseHomeowner || responsePartners;
            if (!response || !response.account) {
                throw new Error();
            }
            const tenant = getTenantByTenantId(response.tenantId);
            getMSALInstance(tenant).setActiveAccount(response.account);
            setTokenInfo({ accessToken: response.accessToken, idToken: response.idToken });
        }
        catch (error) {
            console.error(error);
            loginErrorAlertModal.open();
        }
        finally {
            stopLoading();
        }
    };
    const { clearToken, goToPortal } = useLogout();
    const acquireToken = async (accountInfo) => {
        try {
            startLoading();
            const account = accountInfo || getActiveAccount();
            if (!account) {
                /** 로그아웃에서 리다이렉트 되어 돌아오게 되면 account가 없기 때문에 토큰을 제거함 */
                clearToken();
                return AUTH_STATUS.SUCCESS;
            }
            const { accessToken, idToken } = await acquireTokenSilent(account);
            await setTokenInfo({ accessToken, idToken });
            return AUTH_STATUS.SUCCESS;
        }
        catch (error) {
            console.error(error);
            return AUTH_STATUS.INVALID_ACCESS_TOKEN;
        }
        finally {
            stopLoading();
        }
    };
    const logoutRedirect = async () => {
        try {
            startLoading();
            const accountInfo = getMSALAccountInfo();
            if (!accountInfo) {
                /**
                 * MSAL account cache는 sessionStorage에 저장되어 창마다 분리된다.
                 * 새 창은 accountInfo가 없을 수 있으므로 쿠키 토큰의 tenant로 동일한 provider logout을 실행한다.
                 */
                const authToken = isLocalEnv ? null : getAuthTokenFromCookie();
                const accessTokenJwt = parseJwt(authToken?.accessToken || '');
                if (accessTokenJwt?.tid) {
                    /** provider logout으로 떠나기 전에 앱 쿠키/로컬 토큰을 먼저 정리해 stale 쿠키 재인증을 방지 */
                    clearToken();
                    await logoutRedirectByTenant(getTenantByTenantId(accessTokenJwt.tid));
                    return;
                }
                clearToken();
                goToPortal();
                return;
            }
            /** provider logout으로 떠나기 전에 앱 쿠키/로컬 토큰을 먼저 정리해 stale 쿠키 재인증을 방지 */
            clearToken();
            await logoutRedirectByAccountInfo(accountInfo);
        }
        catch (error) {
            console.error(error);
            /** provider logout이 실패해도 사용자가 잔류하지 않도록 로컬 로그아웃으로 폴백 */
            clearToken();
            goToPortal();
        }
        finally {
            stopLoading();
        }
    };
    return {
        loginRedirect,
        getAuthRedirectPromiseResponse,
        processLogin,
        acquireToken,
        logoutRedirect,
    };
};
